import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * CourseChatWidget.jsx (compact version) — courseId-only
 * Update: support ?courseUuid=... (preferred) -> resolve to PK via Learn, else fallback to localStorage
 * ----------------------------------------------------------------*/

const STORAGE_KEYS = {
    history: (courseKey, userUuid) => `ccw:msgs:${courseKey}:${userUuid}`,
    showCitations: "ccw-show-citations",
    compactMode: "ccw-compact-mode",
    openSettings: "ccw-open-settings",
};

const readBool = (k, def = true) => {
    try {
        const raw = localStorage.getItem(k);
        if (raw == null) return def;
        if (raw === "true" || raw === "1") return true;
        if (raw === "false" || raw === "0") return false;
        return !!JSON.parse(raw);
    } catch {
        return def;
    }
};
const writeBool = (k, v) => {
    try {
        localStorage.setItem(k, v ? "true" : "false");
    } catch {}
};

const compactCss = `
  .bb-container { font-size: 12px; }
  .bb-card h2 { font-size: 14px; margin: 0; }
  .bb-button { font-size: 12px; padding: 4px 8px; border-radius: 6px; }
  .bb-pill { font-size: 12px; padding: 2px 6px; border-radius: 10px; }
  .bb-chat { font-size: 13px; line-height: 1.3; }
  .bb-table { font-size: 12px; border-collapse: collapse; }
  .bb-table th, .bb-table td { padding: 3px 5px; vertical-align: middle; }
`;

function Section({ title, open, onToggle, children, right }) {
    return (
        <div className="bb-card" style={{ padding: 6, border: "1px solid #ddd", borderRadius: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                <h2>{title}</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {right}
                    <button type="button" className="bb-button" onClick={onToggle} aria-expanded={open}>
                        {open ? "Hide" : "Show"}
                    </button>
                </div>
            </div>
            {open && <div style={{ marginTop: 6 }}>{children}</div>}
        </div>
    );
}

function truncate(s = "", n = 42) {
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
function renderPlain(s) {
    const text = String(s || "");
    return text.split(/\n+/).map((p, i) => (
        <p key={i} style={{ margin: 0 }}>
            {p}
        </p>
    ));
}

const normalizeBase = (u) => (u || "").replace(/\/+$/, "");

const CourseChatWidget = ({ backEndUrl, platformUrl, tenantId }) => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = query.get("token") || "";
    const userUuid = query.get("uuid") || "";

    // NEW: prefer courseUuid if present
    const courseUuid = query.get("courseUuid") || "";

    // Existing behavior: localStorage holds PK like "_434_1"
    const storedCourseId = localStorage.getItem("courseId") || "";

    // Effective courseId (PK) used everywhere (history, Learn calls, backend payload)
    const [courseId, setCourseId] = useState(storedCourseId);

    const [role, setRole] = useState("Student");
    const [courseName, setCourseName] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const [openSettings, setOpenSettings] = useState(() => readBool(STORAGE_KEYS.openSettings, false));
    const [showCitations, setShowCitations] = useState(() => readBool(STORAGE_KEYS.showCitations, true));
    const [compactMode, setCompactMode] = useState(() => readBool(STORAGE_KEYS.compactMode, true));

    // Resolve courseUuid -> course PK (id) via Learn (only when courseUuid exists)
    useEffect(() => {
        let cancelled = false;

        async function resolveCourseUuidToPk() {
            if (!courseUuid) return;
            if (!platformUrl || !token) return;

            try {
                const base = normalizeBase(platformUrl);
                // Per your ask: use uuid:<course uuid>
                const url = `${base}/learn/api/public/v1/courses/uuid:${courseUuid}`;
                const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

                const resolvedPk = data?.id || "";
                if (!resolvedPk) return;

                if (cancelled) return;

                setCourseId(resolvedPk);

                // Helpful: also update localStorage so future loads work without the param
                try {
                    localStorage.setItem("courseId", resolvedPk);
                } catch {}

                // If we got the name here, set it immediately
                if (data?.name) setCourseName(data.name);
            } catch (e) {
                // Fallback remains localStorage courseId; just surface a readable error
                console.error("Failed to resolve courseUuid -> courseId (PK)", e);
                const msg = e?.response?.data?.message || e?.message || "Failed to resolve courseUuid.";
                if (!cancelled) setError((prev) => prev || msg);
            }
        }

        resolveCourseUuidToPk();
        return () => {
            cancelled = true;
        };
    }, [courseUuid, platformUrl, token]);

    const historyKey = useMemo(
        () => STORAGE_KEYS.history(courseId || "UNKNOWN_COURSE", userUuid || "anon"),
        [courseId, userUuid]
    );

    const [messages, setMessages] = useState(() => {
        try {
            const raw = sessionStorage.getItem(historyKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) return parsed;
            }
        } catch {}
        return [{ role: "assistant", content: "Hi! Ask me anything about this course’s materials." }];
    });

    const [input, setInput] = useState("");
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const lastPromptRef = useRef("");

    // If courseId changes (e.g., resolved from courseUuid), re-hydrate history for that course
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(historyKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) {
                    setMessages(parsed);
                    return;
                }
            }
        } catch {}
        setMessages([{ role: "assistant", content: "Hi! Ask me anything about this course’s materials." }]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyKey]);

    // Fetch course display name + role once we have a courseId (PK)
    useEffect(() => {
        let cancelled = false;

        async function fetchCourseInfo() {
            if (!courseId || !platformUrl || !token) return;

            try {
                const base = normalizeBase(platformUrl);

                // Get course details (for name display)
                const url = `${base}/learn/api/public/v1/courses/${courseId}`;
                const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!cancelled && data?.name) setCourseName(data.name);

                // Resolve user course role (optional)
                if (userUuid) {
                    try {
                        const memUrl = `${base}/learn/api/public/v1/courses/${courseId}/users/uuid:${userUuid}`;
                        const { data: mem } = await axios.get(memUrl, { headers: { Authorization: `Bearer ${token}` } });
                        if (!cancelled && mem?.courseRoleId) setRole(mem.courseRoleId);
                    } catch {}
                }
            } catch (e) {
                console.error("Failed to fetch course info", e);
            }
        }

        fetchCourseInfo();
        return () => {
            cancelled = true;
        };
    }, [courseId, platformUrl, token, userUuid]);

    useEffect(() => {
        writeBool(STORAGE_KEYS.openSettings, openSettings);
    }, [openSettings]);
    useEffect(() => {
        writeBool(STORAGE_KEYS.showCitations, showCitations);
    }, [showCitations]);
    useEffect(() => {
        writeBool(STORAGE_KEYS.compactMode, compactMode);
    }, [compactMode]);
    useEffect(() => {
        try {
            sessionStorage.setItem(historyKey, JSON.stringify(messages));
        } catch {}
    }, [messages, historyKey]);
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, busy]);

    const guards = useMemo(() => {
        const issues = [];
        if (!backEndUrl) issues.push("Missing backEndUrl prop.");
        if (!token) issues.push("Missing token in URL.");
        if (!platformUrl) issues.push("Missing platformUrl (prop).");

        // NEW behavior: require courseId only after attempting uuid resolution (if provided)
        if (!courseId) {
            if (courseUuid) issues.push("Missing courseId: courseUuid was provided but could not be resolved (or is still resolving).");
            else issues.push("Missing courseId in localStorage.");
        }

        return issues;
    }, [backEndUrl, token, platformUrl, courseId, courseUuid]);

    async function send(customPrompt) {
        setError("");
        const prompt = ((customPrompt ?? input) || "").trim();
        if (!prompt || busy) return;
        if (guards.length) {
            setError(guards.join(" "));
            return;
        }

        lastPromptRef.current = prompt;
        const userMsg = { role: "user", content: prompt };
        setMessages((m) => [...m, userMsg]);
        setInput("");
        setBusy(true);

        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

            const payload = { platformUrl, prompt, role, courseId, timeZone };
            const { data } = await axios.post(`${normalizeBase(backEndUrl)}/chat`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                },
            });

            const assistantMsg = {
                role: "assistant",
                content: data?.answer || "I couldn’t generate an answer.",
                citations: Array.isArray(data?.citations) ? data.citations : [],
            };
            setMessages((m) => [...m, assistantMsg]);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Error calling chat API.";
            setMessages((m) => [...m, { role: "assistant", content: "Sorry—there was an error answering that." }]);
            setError(msg);
        } finally {
            setBusy(false);
        }
    }

    function onKeyDown(e) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }
    function clearChat() {
        setMessages([{ role: "assistant", content: "Chat cleared. Ask me anything about this course’s materials." }]);
        setError("");
        setTimeout(() => inputRef.current?.focus(), 0);
    }

    const btnPrimary = {
        padding: "6px 8px",
        borderRadius: 6,
        border: "1px solid #2c6bed",
        background: "#2c6bed",
        color: "#fff",
        fontWeight: 600,
        cursor: "pointer",
        fontSize: 12,
    };
    const btnSecondary = {
        padding: "4px 6px",
        borderRadius: 6,
        border: "1px solid #ddd",
        background: "#fff",
        color: "#333",
        fontWeight: 600,
        cursor: "pointer",
        fontSize: 12,
    };
    const bubbleBase = {
        maxWidth: "min(1200px, 96vw)",
        padding: "6px 8px",
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        fontSize: 13,
    };
    const bubbleUser = { marginLeft: "auto", background: "#eef2ff", border: "1px solid #e0e7ff" };
    const bubbleAssistant = { marginRight: "auto", background: "#f9fafb", border: "1px solid #eee" };
    const bannerError = {
        margin: "6px 0",
        padding: "6px 8px",
        borderRadius: 6,
        background: "#fff3f3",
        border: "1px solid #ffd4d4",
        color: "#8a0000",
        fontSize: 12,
    };

    return (
        <div className="bb-container" style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 8 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Header */}
            <div className="bb-card" style={{ padding: 6, border: "1px solid #ddd", borderRadius: 6 }}>
                <div className="bb-header" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <h2 style={{ fontSize: 15 }}>
                        <b>Course AI Assistant</b>
                    </h2>
                    <span
                        className="bb-pill"
                        style={{
                            background: role.toLowerCase().startsWith("instructor") ? "#eef7ff" : "#f6fff0",
                            border: "1px solid #ddd",
                        }}
                    >
            Role: {role}
          </span>
                    <span className="bb-pill" style={{ background: "#fafafa", border: "1px solid #eee" }}>
            Course: {courseName || courseId || (courseUuid ? `uuid:${courseUuid}` : "UNKNOWN")}
          </span>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        <button
                            type="button"
                            className="bb-button"
                            onClick={() => setOpenSettings((o) => !o)}
                            aria-pressed={openSettings}
                        >
                            {openSettings ? "Hide Settings" : "Show Settings"}
                        </button>
                        <button className="bb-button" onClick={clearChat} style={btnSecondary}>
                            Clear
                        </button>
                    </div>
                </div>

                {guards.length > 0 && (
                    <div className="bb-error" style={bannerError}>
                        <strong>Setup issue:</strong> {guards.join(" ")}
                    </div>
                )}
                {error && <div className="bb-error" style={bannerError}>{error}</div>}
            </div>

            {/* Settings */}
            <Section title={<span><b>Settings</b></span>} open={openSettings} onToggle={() => setOpenSettings((o) => !o)}>
                <div style={{ display: "grid", gap: 6, fontSize: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={showCitations} onChange={(e) => setShowCitations(e.target.checked)} />
                        Show sources (citations)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={compactMode} onChange={(e) => setCompactMode(e.target.checked)} />
                        Compact mode
                    </label>
                </div>
            </Section>

            {/* Chat */}
            <div
                ref={listRef}
                className="bb-chat"
                style={{
                    border: "1px solid #e6e6e6",
                    borderRadius: 8,
                    padding: 8,
                    height: compactMode ? 520 : 650,
                    overflowY: "auto",
                    background: "#fff",
                }}
            >
                {messages.map((m, i) => (
                    <div key={i} style={{ margin: "6px 0", display: "flex" }}>
                        <div className={`bb-bubble ${m.role}`} style={{ ...bubbleBase, ...(m.role === "user" ? bubbleUser : bubbleAssistant) }}>
                            <div className="bb-content">{renderPlain(m.content)}</div>
                            {showCitations && Array.isArray(m.citations) && m.citations.length > 0 && (
                                <div className="bb-citations" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {m.citations.map((c, j) => (
                                        <a
                                            key={j}
                                            href={c.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ fontSize: 11, textDecoration: "none", borderBottom: "1px dotted #999" }}
                                            title={c.title}
                                        >
                                            [{truncate(c.title, 30)}]
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {busy && (
                    <div style={{ margin: "6px 0", display: "flex" }}>
                        <div className="bb-bubble assistant" style={{ ...bubbleBase, ...bubbleAssistant }}>
                            <div className="bb-content">Thinking…</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Composer */}
            <div className="bb-card" style={{ padding: 6, border: "1px solid #ddd", borderRadius: 6 }}>
                <div className="bb-input-row" style={{ display: "flex", gap: 6 }}>
          <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about readings, assignments, due dates, or where to find something…"
              rows={compactMode ? 2 : 3}
              style={{
                  flex: 1,
                  resize: "vertical",
                  minHeight: 36,
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  fontFamily: "inherit",
                  fontSize: 13,
              }}
          />
                    <button className="bb-button primary" onClick={() => send()} disabled={busy || !!guards.length} style={btnPrimary}>
                        {busy ? "Thinking…" : "Ask"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CourseChatWidget;